/**
 * The practice-mode writer (13-08): the ONE door every dashboard write goes through.
 *
 * `getWriter()` returns `{ commitVerb, releaseBatch, retireBatch, retryBulkAudit, verifyVL, verifyVLBatch,
 * correctVL, postVlAudit }` (plus the outbox's bindings and storage):
 *   - **live** — the `actionService` functions, unchanged. Only when
 *     `import.meta.env.VITE_QMS_LIVE_WRITES === 'true'`, which the production build sets at 13-13
 *     (`.env.production`, committed then). Nothing else turns it on.
 *   - **practice** (the default, and always in dev) — dry-run twins with the same signatures.
 *     They build exactly what live would send (the same verb builders, the same `canDoVerb`
 *     re-check, the same `[a:<id>]` tags), `console.info` the patches and audit drafts, wait a
 *     short simulated latency, and report success. They never touch Graph, so building and
 *     clicking around can never write a live row (memory: never probe a write endpoint on a live,
 *     people-facing object). `PracticeRibbon` says so across the shell.
 *
 * Practice-mode outbox entries live under their own storage key prefix, so an entry made while
 * practising can never be replayed as a live write later in the same browser.
 *
 * 13-23 adds the register record methods (D-13-L-4, -5, -9): `lodgeRecord`, `commitRecordVerb`,
 * `postRecordAudit` and `retryRecordAudit`. Live delegates to `recordService` and, whenever a landed write still
 * owes sibling rows, persists them per user (`recordAuditOwed`, `qms.recordAudit.v1`) before it returns. Practice
 * writes nothing to Graph: a practice lodge gets a ref in the practice-only range `<PREFIX>-P<n>` (D-13-23-1) and
 * is kept under `qms.practice.records.v1`, so the record page can open it in practice; `?simulate=412` is honoured
 * in a dev build, on the practice writer only (D-13-12-7). Register verbs never go through the outbox.
 */

import {
  CommitError,
  assertFollowUpFields,
  buildVlVerify,
  commitVerb as liveCommitVerb,
  correctVL as liveCorrectVL,
  createFollowUp as liveCreateFollowUp,
  findCommitted as liveFindCommitted,
  followUpAuditRow,
  postFollowUpAudit as livePostFollowUpAudit,
  outboxBindings as liveOutboxBindings,
  postVlAudit as livePostVlAudit,
  releaseBatch as liveReleaseBatch,
  retireBatch as liveRetireBatch,
  retryBulkAudit as liveRetryBulkAudit,
  tagVlAudit,
  verifyVL as liveVerifyVL,
} from './actionService';
import type {
  ActionIO,
  BulkRunOptions,
  BulkSummary,
  FollowUpCreate,
  FollowUpCreated,
  VlAuditPending,
  VlCorrectOpts,
  VlVerifyVerdict,
} from './actionService';
import { RELEASE_BATCH_SIZE } from '../config/actionRegister';
import { chunk } from '../lib/batch';
import { canDoVerb } from '../lib/actionAuthority';
import { vlCorrect } from '../lib/vlVerbs';
import type { VlCorrectVerdict } from '../lib/vlVerbs';
import { buildVerb, VerbError } from '../lib/actionVerbs';
import { actionTag, auditToPost, browserStorage } from '../lib/outbox';
import type { CommitFn, CommitProgress, CommitResult, OutboxEntry, OutboxStorage } from '../lib/outbox';
import type { ActionRow, Actor, AuditRowDraft, VerificationRow } from '../types/actionRegister';
import {
  RecordConflictError,
  assertLodgeFields,
  checkGate,
  commitRecordVerb as liveCommitRecord,
  createRecord as liveCreateRecord,
  postRecordAudit as livePostRecordAudit,
  prepareSibling,
} from './recordService';
import type { RecordGate, RecordIO, SiblingRow } from './recordService';
import {
  AuditConflictError,
  assertAuditPatch,
  checkAuditGate,
  commitAuditVerb as liveCommitAudit,
  createAudit as liveCreateAudit,
  postAuditRows as livePostAuditRows,
  prepareAuditSibling,
} from './internalAuditService';
import type { AuditGate } from './internalAuditService';
import { composePracticeAuditRef, isAuditRef, readAuditRef } from '../lib/auditQuery';
import type { AuditRow } from '../lib/auditVerbs';
import { defaultRecordAuditStore, retryOwed } from './recordAuditOwed';
import type { RecordAuditStore, RetryOwedResult } from './recordAuditOwed';
import { parseSimulateFlag } from '../lib/releaseFilters';
import { composePracticeRef, isPracticeRef, parsePracticeRef, parseRef } from '../lib/recordQuery';
import type { LodgeResult, RecordRegister, RecordRow, SiblingDraft, VerbResult } from '../types/records';

export type WriteMode = 'live' | 'practice';

/** The env flag that, and only that, makes writes live. */
export const LIVE_WRITES_FLAG = 'VITE_QMS_LIVE_WRITES';

/** How long a practice write "takes", so the UI's in-flight states are exercised. */
export const PRACTICE_LATENCY_MS = 350;

/** Practice outbox entries are stored under this prefix (never the live key). */
export const PRACTICE_STORAGE_PREFIX = 'qms.practice.';

/** Bulk Verification Log verify (13-11): one row at a time, each with its own If-Match. */
export interface VlBatchSummary {
  verdict: VlVerifyVerdict;
  /** Row ids whose PATCH landed (their audit rows may still be in `auditPending`). */
  applied: number[];
  /** 412: changed by someone else since it was read. Refetch. */
  conflicts: number[];
  failed: Array<{ id: number; status: number | null; reason: string }>;
  /** Refused before sending (the verify builder said no). */
  refused: Array<{ id: number; code: string }>;
  /** The PATCH landed but these audit drafts did not post. Re-post with `Writer.postVlAudit` (de-duped). */
  auditPending: VlAuditPending;
}

type VerifyOpts = { reason?: string; now?: Date | string | number };

/**
 * 13-12, PRACTICE ONLY: outcomes the dry-run sitting pretends Graph returned, so the result line
 * and the retry path can be seen without a tenant. The live writer ignores this field entirely
 * (its `runBulk` never reads it); the Release page sets it only from a dev-only query flag.
 */
export interface PracticeSimulation {
  /** Rows answered 412 (changed by someone else). */
  conflicts?: number;
  /** Rows answered 429 on every round: re-batched once, then failed as `throttled`. */
  throttled?: number;
  /** Rows whose PATCH lands but whose audit rows do not post (`auditPending`). */
  auditFail?: number;
  /** The simulated Retry-After on the throttled rows (default 5 s). */
  retryAfterMs?: number;
}

export type BulkOpts = BulkRunOptions & { simulate?: PracticeSimulation };

export interface OutboxBindings {
  commit: CommitFn;
  findCommitted: (actionId: string, entry: OutboxEntry) => Promise<boolean>;
  retryAfterMsFor: (actionId: string) => number | undefined;
}

export interface Writer {
  mode: WriteMode;
  commitVerb: (io: ActionIO, entry: OutboxEntry, progress: CommitProgress) => Promise<CommitResult>;
  findCommitted: (io: ActionIO, actionId: string, entry: OutboxEntry) => Promise<boolean>;
  releaseBatch: (io: ActionIO, rows: readonly ActionRow[], note: string, actor: Actor, opts?: BulkOpts) => Promise<BulkSummary>;
  retireBatch: (io: ActionIO, rows: readonly ActionRow[], note: string, actor: Actor, opts?: BulkOpts) => Promise<BulkSummary>;
  /** 13-12: post a sitting's `auditPending` again (de-duped); returns what is still not posted. */
  retryBulkAudit: (io: ActionIO, sittingId: string, pending: BulkSummary['auditPending']) => Promise<BulkSummary['auditPending']>;
  verifyVL: (io: ActionIO, row: VerificationRow, verdict: VlVerifyVerdict, actor: Actor, opts?: VerifyOpts) => Promise<CommitResult>;
  verifyVLBatch: (
    io: ActionIO,
    rows: readonly VerificationRow[],
    verdict: VlVerifyVerdict,
    actor: Actor,
    opts?: VerifyOpts,
  ) => Promise<VlBatchSummary>;
  /**
   * 13-13 (ISS-194): re-post Verification Log audit drafts that failed after their PATCH landed
   * (a single verify / reject / correction's `CommitError.remainingAudit`, or a bulk
   * `auditPending`), de-duped on a fresh read. Returns what is still not posted.
   */
  postVlAudit: (io: ActionIO, pending: VlAuditPending) => Promise<VlAuditPending>;
  /** 13-11.1 (D-13-R-5): correct a Verification Log verdict the same Melbourne day. Synchronous. */
  correctVL: (
    io: ActionIO,
    row: VerificationRow,
    newVerdict: VlCorrectVerdict,
    reason: string,
    actor: Actor,
    opts?: VlCorrectOpts,
  ) => Promise<CommitResult>;
  /**
   * 13-23 (D-13-L-8): create a register record from `buildLodge`'s result, then its sibling rows under the composed
   * `ParentRef`. Rows that did not post are owed (persisted per user) and returned in `auditPending`.
   */
  lodgeRecord: (io: RecordIO, register: RecordRegister, lodge: LodgeResult, actor: Actor) => Promise<RecordLodgeResult>;
  /**
   * 13-23 (D-13-L-4): one verb save, synchronous, `If-Match`, only the patch columns, then the sibling drafts. The
   * authority gate runs again here (D-13-07-6). A 412 throws `RecordConflictError` (fresh row + columns). An empty
   * patch is the sibling-only path (13-20's rows-only save).
   */
  commitRecordVerb: (
    io: RecordIO,
    ref: string,
    eTag: string,
    result: VerbResult,
    actor: Actor,
    gate: RecordGate,
  ) => Promise<RecordCommitResult>;
  /** 13-23 (D-13-L-5): post owed sibling drafts for one record, de-duped on a fresh read. Returns what is still owed. */
  postRecordAudit: (io: RecordIO, ref: string, drafts: readonly SiblingDraft[]) => Promise<SiblingDraft[]>;
  /**
   * 13.2-10 (D-13.2-P-10): post a save's rows that belong on ANOTHER record's trail (`closeAsDuplicate`'s
   * `keptSiblings`, the kept record's Note), after the save itself landed. Each draft is prepared under `ref`. Never
   * throws: a row that did not post is owed under `ref` (persisted per user, as `commitRecordVerb`'s are, D-13-L-5)
   * and returned in `auditPending`, then retried on load or when `ref` is opened. Practice appends them to the
   * practice record when `ref` is one, and owes nothing.
   */
  postRecordSiblings: (io: RecordIO, ref: string, drafts: readonly SiblingDraft[], actor: Actor) => Promise<RecordCommitResult>;
  /**
   * 13-23 (D-13-L-5): retry the signed-in actor's persisted owed rows (all of them on load; one record's with `ref`
   * on the record page). Never another user's (D-13-06-2).
   */
  retryRecordAudit: (io: RecordIO, actor: Pick<Actor, 'upn'>, opts?: { ref?: string }) => Promise<RetryOwedResult>;
  /**
   * 13-32: schedule an Internal Audit from `buildAuditVerb('schedule', …)`'s result, then its sibling rows under
   * `AUD-<id>`. Rows that did not post are owed (persisted per user, the same store), returned in `auditPending`.
   */
  scheduleAudit: (io: RecordIO, result: VerbResult, actor: Actor) => Promise<RecordLodgeResult>;
  /** 13-32: one audit verb save, synchronous, `If-Match`, the gate again (D-13-L-4); a 412 throws `AuditConflictError`. */
  commitAuditVerb: (io: RecordIO, ref: string, eTag: string, result: VerbResult, actor: Actor, gate: AuditGate) => Promise<RecordCommitResult>;
  /**
   * 13.2-07 (D-13.2-P-7): create an incident follow-up from `buildFollowUp`'s output: the Action Register item, then
   * its "created" audit row (no `ActorUPN`). Synchronous, no outbox and no undo (like the VL writes). A failed audit
   * row after the item landed throws `CommitError('audit')` with the row owed in `remainingAudit`; the item is never
   * re-sent.
   */
  createFollowUp: (io: ActionIO, built: FollowUpCreate) => Promise<FollowUpCreated>;
  /** 13.2-07: post a follow-up's owed audit row(s) again, de-duped on a fresh read. Returns what is still owed. */
  postFollowUpAudit: (io: ActionIO, drafts: readonly AuditRowDraft[]) => Promise<AuditRowDraft[]>;
  /** What the shell spreads into `createOutbox`. */
  outboxBindings: (io: ActionIO) => OutboxBindings;
  /** The outbox's storage for this mode (practice entries are kept apart from live ones). */
  outboxStorage: () => OutboxStorage;
}

/** What a register lodge returns (13-23). */
export interface RecordLodgeResult {
  ref: string;
  eTag?: string;
  /** Sibling drafts still owed after the parent landed (already persisted per user when live). */
  auditPending: SiblingDraft[];
}

/** What a register verb save returns (13-23), `CommitResult`-like. */
export interface RecordCommitResult {
  eTag?: string;
  /** Sibling drafts still owed after the PATCH landed (already persisted per user when live). */
  auditPending: SiblingDraft[];
}

export interface WriterOptions {
  /** Default `import.meta.env`. */
  env?: Record<string, unknown>;
  /** Practice latency (tests pass an instant one). */
  sleep?: (ms: number) => Promise<void>;
  /** Default `console.info`. */
  log?: (message: string, detail: unknown) => void;
  /** Where live owed register audit rows are kept (default this browser's `qms.recordAudit.v1`). */
  recordAudit?: RecordAuditStore;
  /** Where practice register records are kept (default `qms.practice.records.v1` in localStorage). */
  practiceRecords?: PracticeRecordStore;
  /** 13-32: where practice audits are kept (default `qms.practice.audits.v1` in localStorage). */
  practiceAudits?: PracticeAuditStore;
  /**
   * PRACTICE ONLY (D-13-12-7): the dev `?simulate=` flag, e.g. `'412'`. Default: read from the URL, and only in a
   * dev build. The live writer never reads it.
   */
  simulate?: () => string | null | undefined;
}

function defaultEnv(): Record<string, unknown> {
  return (import.meta.env ?? {}) as Record<string, unknown>;
}

/** `live` only when the flag is exactly the string `'true'`; anything else is practice. */
export function writeMode(env: Record<string, unknown> = defaultEnv()): WriteMode {
  return env[LIVE_WRITES_FLAG] === 'true' ? 'live' : 'practice';
}

export function isPracticeMode(env?: Record<string, unknown>): boolean {
  return writeMode(env) === 'practice';
}

const realSleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

// ---------------------------------------------------------------------------
// Live
// ---------------------------------------------------------------------------

/** Bulk VL verify, live: sequential `verifyVL`, every outcome reported, nothing dropped. */
async function liveVerifyVLBatch(
  io: ActionIO,
  rows: readonly VerificationRow[],
  verdict: VlVerifyVerdict,
  actor: Actor,
  opts: VerifyOpts = {},
): Promise<VlBatchSummary> {
  const summary: VlBatchSummary = { verdict, applied: [], conflicts: [], failed: [], refused: [], auditPending: [] };
  const seen = new Set<number>();
  for (const row of rows) {
    if (seen.has(row.id)) continue;
    seen.add(row.id);
    try {
      await liveVerifyVL(io, row, verdict, actor, opts);
      summary.applied.push(row.id);
    } catch (err) {
      if (err instanceof VerbError) {
        summary.refused.push({ id: row.id, code: err.code });
      } else if (err instanceof CommitError && err.stage === 'audit') {
        summary.applied.push(row.id);
        summary.auditPending.push({ id: row.id, drafts: err.remainingAudit });
      } else if (err instanceof CommitError && err.statusCode === 412) {
        summary.conflicts.push(row.id);
      } else {
        const status = err instanceof CommitError ? (err.statusCode ?? null) : null;
        summary.failed.push({ id: row.id, status, reason: err instanceof Error ? err.message : String(err) });
      }
    }
  }
  return summary;
}

/** 13-32: an owed entry is posted by its own list's poster (an `AUD-` ref to Internal Audits Audit Log). */
const postOwed: Writer['postRecordAudit'] = (io, ref, drafts) => (isAuditRef(ref) ? livePostAuditRows(io, ref, drafts) : livePostRecordAudit(io, ref, drafts));

function liveRecordMethods(
  opts: WriterOptions,
): Pick<
  Writer,
  'lodgeRecord' | 'commitRecordVerb' | 'postRecordAudit' | 'postRecordSiblings' | 'retryRecordAudit' | 'scheduleAudit' | 'commitAuditVerb'
> {
  const store = () => opts.recordAudit ?? defaultRecordAuditStore();
  return {
    scheduleAudit: async (io, result, actor) => {
      const r = await liveCreateAudit(io, result, actor);
      if (r.auditPending.length > 0) store().owe(actor.upn, r.ref, r.auditPending);
      return { ref: r.ref, ...(r.eTag ? { eTag: r.eTag } : {}), auditPending: r.auditPending };
    },
    commitAuditVerb: async (io, ref, eTag, result, actor, gate) => {
      const r = await liveCommitAudit(io, ref, eTag, result, actor, gate);
      const canonical = readAuditRef(ref)?.ref ?? ref;
      if (r.auditPending.length > 0) store().owe(actor.upn, canonical, r.auditPending);
      return { ...(r.eTag ? { eTag: r.eTag } : {}), auditPending: r.auditPending };
    },
    lodgeRecord: async (io, register, lodge, actor) => {
      const r = await liveCreateRecord(io, register, lodge, actor);
      if (r.auditPending.length > 0) store().owe(actor.upn, r.ref, r.auditPending);
      return { ref: r.ref, ...(r.eTag ? { eTag: r.eTag } : {}), auditPending: r.auditPending };
    },
    commitRecordVerb: async (io, ref, eTag, result, actor, gate) => {
      const r = await liveCommitRecord(io, ref, eTag, result, actor, gate);
      if (r.auditPending.length > 0) store().owe(actor.upn, parseRef(ref).ref, r.auditPending);
      return { ...(r.eTag ? { eTag: r.eTag } : {}), auditPending: r.auditPending };
    },
    postRecordAudit: postOwed,
    postRecordSiblings: async (io, ref, drafts, actor) => {
      const canonical = parseRef(ref).ref;
      const prepared = drafts.map((d) => prepareSibling(d, canonical, actor));
      if (prepared.length === 0) return { auditPending: [] };
      let still: SiblingDraft[];
      try {
        still = await livePostRecordAudit(io, canonical, prepared);
      } catch {
        still = prepared;
      }
      if (still.length > 0) store().owe(actor.upn, canonical, still);
      return { auditPending: still };
    },
    retryRecordAudit: (io, actor, o = {}) => retryOwed(io, actor, { store: store(), post: postOwed, ...(o.ref ? { ref: o.ref } : {}) }),
  };
}

function liveWriter(opts: WriterOptions = {}): Writer {
  return {
    ...liveRecordMethods(opts),
    mode: 'live',
    commitVerb: liveCommitVerb,
    findCommitted: liveFindCommitted,
    releaseBatch: liveReleaseBatch,
    retireBatch: liveRetireBatch,
    retryBulkAudit: liveRetryBulkAudit,
    verifyVL: liveVerifyVL,
    verifyVLBatch: liveVerifyVLBatch,
    correctVL: liveCorrectVL,
    postVlAudit: livePostVlAudit,
    createFollowUp: liveCreateFollowUp,
    postFollowUpAudit: livePostFollowUpAudit,
    outboxBindings: liveOutboxBindings,
    outboxStorage: browserStorage,
  };
}

// ---------------------------------------------------------------------------
// Practice (dry-run twins)
// ---------------------------------------------------------------------------

/** Wraps a storage so every key gets the practice prefix. */
export function practiceStorage(inner: OutboxStorage): OutboxStorage {
  const k = (key: string) => PRACTICE_STORAGE_PREFIX + key;
  return {
    get: (key) => inner.get(k(key)),
    set: (key, value) => inner.set(k(key), value),
    remove: (key) => inner.remove(k(key)),
  };
}

/** How long each practice `$batch` chunk "takes", so the per-chunk progress line is visible. */
export const PRACTICE_CHUNK_MS = 140;

type SimOutcome = 'ok' | 'conflict' | 'throttled' | 'auditFail';

/**
 * Which of `n` rows get a simulated outcome: conflicts, then throttled, then audit failures, each
 * spread evenly across the sitting (deterministic). Everything else is `ok`. No simulation → all ok.
 */
export function simulatedOutcomes(n: number, sim: PracticeSimulation | undefined): SimOutcome[] {
  const out: SimOutcome[] = Array.from({ length: n }, () => 'ok');
  if (!sim || n === 0) return out;
  const place = (count: number | undefined, kind: SimOutcome) => {
    const c = Math.min(Math.max(0, Math.floor(count ?? 0)), out.filter((o) => o === 'ok').length);
    for (let k = 0; k < c; k += 1) {
      let i = Math.floor(((k + 0.5) * n) / c) % n;
      while (out[i] !== 'ok') i = (i + 1) % n;
      out[i] = kind;
    }
  };
  place(sim.conflicts, 'conflict');
  place(sim.throttled, 'throttled');
  place(sim.auditFail, 'auditFail');
  return out;
}

function practiceETag(prev: string | null | undefined): string {
  return `"practice-${prev ? String(prev).replace(/"/g, '') : 'new'}-${Date.now().toString(36)}"`;
}

// ---------------------------------------------------------------------------
// Practice register records (13-23, D-13-L-9, D-13-23-1)
// ---------------------------------------------------------------------------

/** The practice records' key under the practice prefix: `qms.practice.records.v1`. */
export const PRACTICE_RECORDS_KEY = 'records.v1';

/** A record lodged in practice: never on the tenant, never replayed live. */
export interface PracticeRecord {
  /** `<PREFIX>-P<n>`: the practice-only range (never matches a live ref). */
  ref: string;
  register: RecordRegister;
  eTag: string;
  fields: Record<string, unknown>;
  siblings: SiblingDraft[];
  lodgedAt: string;
  /** Lower-cased UPN of who practised it. */
  upn: string;
}

export interface PracticeRecordStore {
  /** The next practice ref for `register` (one past the highest stored). */
  nextRef(register: RecordRegister): string;
  put(record: PracticeRecord): void;
  get(ref: string): PracticeRecord | undefined;
  list(): PracticeRecord[];
}

function isPracticeRecord(x: unknown): x is PracticeRecord {
  if (typeof x !== 'object' || x === null) return false;
  const r = x as Partial<PracticeRecord>;
  return isPracticeRef(r.ref) && typeof r.register === 'string' && typeof r.fields === 'object' && Array.isArray(r.siblings);
}

/** Practice records over `inner` (default `localStorage`), under the practice prefix. Storage failures fall back to memory. */
export function practiceRecordStore(inner?: OutboxStorage): PracticeRecordStore {
  let storage: OutboxStorage | null;
  try {
    storage = practiceStorage(inner ?? browserStorage());
  } catch {
    storage = null;
  }
  let memory: PracticeRecord[] = [];
  const read = (): PracticeRecord[] => {
    if (!storage) return [...memory];
    try {
      const raw = storage.get(PRACTICE_RECORDS_KEY);
      const parsed: unknown = raw ? JSON.parse(raw) : [];
      memory = Array.isArray(parsed) ? parsed.filter(isPracticeRecord) : [];
    } catch {
      storage = null;
    }
    return [...memory];
  };
  const write = (rows: PracticeRecord[]) => {
    memory = rows;
    if (!storage) return;
    try {
      storage.set(PRACTICE_RECORDS_KEY, JSON.stringify(rows));
    } catch {
      storage = null;
    }
  };
  return {
    nextRef(register) {
      const highest = read()
        .filter((r) => r.register === register)
        .reduce((max, r) => Math.max(max, parsePracticeRef(r.ref).id), 0);
      return composePracticeRef(register, highest + 1);
    },
    put(record) {
      write([...read().filter((r) => r.ref !== record.ref), record]);
    },
    get(ref) {
      if (!isPracticeRef(ref)) return undefined;
      const canonical = parsePracticeRef(ref).ref;
      return read().find((r) => r.ref === canonical);
    },
    list: () => read(),
  };
}

/**
 * 13-24 (D-13-24-7): what a practice lodge keeps in this browser. A client's name, picked from the client lists or
 * typed, never goes into storage (D-13-L-7): `RecipientName` is kept as a placeholder. The console line still shows
 * what live would send.
 */
export const PRACTICE_CLIENT_NAME = '(client name not kept in practice)';

export function practiceStoredFields(fields: Readonly<Record<string, unknown>>): Record<string, unknown> {
  const out: Record<string, unknown> = { ...fields };
  if (typeof out.RecipientName === 'string' && out.RecipientName !== '') out.RecipientName = PRACTICE_CLIENT_NAME;
  return out;
}

/** The practice record behind a practice ref as the record page reads it, or undefined. */
export function readPracticeRecord(
  ref: string,
  store: PracticeRecordStore = practiceRecordStore(),
): { row: RecordRow; siblings: SiblingRow[] } | undefined {
  const rec = store.get(ref);
  if (!rec) return undefined;
  const { id } = parsePracticeRef(rec.ref);
  const row: RecordRow = { id, ref: rec.ref, eTag: rec.eTag, register: rec.register, fields: { ...rec.fields } };
  return { row, siblings: practiceSiblingRows(rec.ref, rec.siblings, rec.upn) };
}

/** Practice sibling drafts as the record page reads sibling rows (in the order they were made). */
function practiceSiblingRows(ref: string, drafts: readonly SiblingDraft[], upn: string): SiblingRow[] {
  return drafts.map((d, i) => ({
    id: i + 1,
    title: d.Title,
    parentRef: ref,
    entryTimestamp: d.EntryTimestamp,
    entryType: d.EntryType,
    logKind: d.LogKind ?? null,
    fieldChanged: d.FieldChanged ?? null,
    oldValue: d.OldValue ?? null,
    newValue: d.NewValue ?? null,
    note: d.Note,
    actorUpn: d.ActorUPN || null,
    actorLookupId: d.ActorLookupId ?? null,
    authorEmail: upn || null,
    created: d.EntryTimestamp,
  }));
}

// ---------------------------------------------------------------------------
// Practice Internal Audits (13-32): `AUD-P<n>`, kept apart from the register records
// ---------------------------------------------------------------------------

/** The practice audits' key under the practice prefix: `qms.practice.audits.v1`. */
export const PRACTICE_AUDITS_KEY = 'audits.v1';

/** An audit scheduled in practice: never on the tenant, never replayed live. */
export interface PracticeAudit {
  /** `AUD-P<n>`. */
  ref: string;
  eTag: string;
  fields: Record<string, unknown>;
  siblings: SiblingDraft[];
  scheduledAt: string;
  /** Lower-cased UPN of who practised it. */
  upn: string;
}

export interface PracticeAuditStore {
  nextRef(): string;
  put(audit: PracticeAudit): void;
  get(ref: string): PracticeAudit | undefined;
  list(): PracticeAudit[];
}

function isPracticeAudit(x: unknown): x is PracticeAudit {
  if (typeof x !== 'object' || x === null) return false;
  const a = x as Partial<PracticeAudit>;
  return readAuditRef(a.ref)?.practice === true && typeof a.fields === 'object' && Array.isArray(a.siblings);
}

/** Practice audits over `inner` (default `localStorage`), under the practice prefix; storage failures fall back to memory. */
export function practiceAuditStore(inner?: OutboxStorage): PracticeAuditStore {
  let storage: OutboxStorage | null;
  try {
    storage = practiceStorage(inner ?? browserStorage());
  } catch {
    storage = null;
  }
  let memory: PracticeAudit[] = [];
  const read = (): PracticeAudit[] => {
    if (!storage) return [...memory];
    try {
      const raw = storage.get(PRACTICE_AUDITS_KEY);
      const parsed: unknown = raw ? JSON.parse(raw) : [];
      memory = Array.isArray(parsed) ? parsed.filter(isPracticeAudit) : [];
    } catch {
      storage = null;
    }
    return [...memory];
  };
  const write = (rows: PracticeAudit[]) => {
    memory = rows;
    if (!storage) return;
    try {
      storage.set(PRACTICE_AUDITS_KEY, JSON.stringify(rows));
    } catch {
      storage = null;
    }
  };
  return {
    nextRef() {
      const highest = read().reduce((max, a) => Math.max(max, readAuditRef(a.ref)?.id ?? 0), 0);
      return composePracticeAuditRef(highest + 1);
    },
    put(audit) {
      write([...read().filter((a) => a.ref !== audit.ref), audit]);
    },
    get(ref) {
      const parsed = readAuditRef(ref);
      if (!parsed?.practice) return undefined;
      return read().find((a) => a.ref === parsed.ref);
    },
    list: () => read(),
  };
}

/** A practice audit as the audit page reads it. */
function practiceAuditRow(a: PracticeAudit): AuditRow {
  return { id: readAuditRef(a.ref)?.id ?? 0, ref: a.ref, eTag: a.eTag, fields: { ...a.fields } };
}

/** The practice audit behind `AUD-P<n>`, with its rows, or undefined. */
export function readPracticeAudit(
  ref: string,
  store: PracticeAuditStore = practiceAuditStore(),
): { row: AuditRow; siblings: SiblingRow[] } | undefined {
  const a = store.get(ref);
  return a ? { row: practiceAuditRow(a), siblings: practiceSiblingRows(a.ref, a.siblings, a.upn) } : undefined;
}

/** Every practice audit in this browser (the `/audits` list adds them in practice mode). */
export function listPracticeAudits(store: PracticeAuditStore = practiceAuditStore()): AuditRow[] {
  return store.list().map(practiceAuditRow);
}

/** The dev `?simulate=` flag (D-13-12-7): only in a dev build, read from the URL. */
function defaultSimulate(env: Record<string, unknown>): () => string | null | undefined {
  return () => {
    if (env.DEV !== true) return null;
    try {
      return new URLSearchParams(globalThis.location?.search ?? '').get('simulate');
    } catch {
      return null;
    }
  };
}

function practiceWriter(opts: WriterOptions): Writer {
  const sleep = opts.sleep ?? realSleep;
  const log = opts.log ?? ((message: string, detail: unknown) => console.info(message, detail));
  const say = (what: string, detail: unknown) => log(`[practice mode] ${what} (nothing was saved)`, detail);

  const commitVerb = async (_io: ActionIO, entry: OutboxEntry, progress: CommitProgress): Promise<CommitResult> => {
    say(`would commit ${entry.verb} on ACT-${entry.itemId}`, {
      itemId: entry.itemId,
      ifMatch: entry.eTag,
      patch: entry.patched ? '(already applied)' : entry.patch,
      audit: auditToPost(entry).map((a) => a.draft),
    });
    await sleep(PRACTICE_LATENCY_MS);
    if (!entry.patched) progress.patched();
    for (const { index } of auditToPost(entry)) progress.auditPosted(index);
    return { eTag: practiceETag(entry.eTag) };
  };

  // Nothing practised ever reaches the server, so nothing is ever "already committed".
  const findCommitted = async (): Promise<boolean> => false;

  const bulk = async (
    verb: 'release' | 'retire',
    rows: readonly ActionRow[],
    note: string,
    actor: Actor,
    bulkOpts: BulkOpts = {},
  ): Promise<BulkSummary> => {
    const at = new Date(bulkOpts.now ?? Date.now()).toISOString();
    const sittingId = crypto.randomUUID();
    const tag = actionTag(sittingId);
    const summary: BulkSummary = {
      verb,
      sittingId,
      applied: [],
      conflicts: [],
      retried: 0,
      failed: [],
      refused: [],
      auditPending: [],
      eTags: {},
    };
    const seen = new Set<number>();
    const wouldSend: Array<{ id: number; ifMatch: string; patch: Record<string, unknown>; audit: AuditRowDraft[] }> = [];
    for (const row of rows) {
      if (seen.has(row.id)) continue;
      seen.add(row.id);
      // The same refusals as the live sitting (D-13-07-6), so practice shows what live would do.
      const allowed = canDoVerb(verb, row, actor);
      if (!allowed.ok) {
        summary.refused.push({ id: row.id, code: allowed.reason });
        continue;
      }
      try {
        const result = buildVerb(verb, row, actor, { note, bulk: true }, at);
        if (Object.keys(result.patch).length === 0) {
          summary.refused.push({ id: row.id, code: 'no-change' });
          continue;
        }
        const audit = result.audit.map((d) => ({ ...d, note: d.note ? `${d.note} ${tag}` : tag }));
        wouldSend.push({ id: row.id, ifMatch: row.eTag, patch: result.patch, audit });
      } catch (err) {
        if (err instanceof VerbError) summary.refused.push({ id: row.id, code: err.code });
        else throw err;
      }
    }

    // 13-12: simulated outcomes (dev-only), spread across the sitting so they land in real chunks.
    const outcome = simulatedOutcomes(wouldSend.length, bulkOpts.simulate);
    const groups = chunk(wouldSend.map((w, i) => ({ ...w, outcome: outcome[i] })), RELEASE_BATCH_SIZE);
    say(`would ${verb} ${wouldSend.length} row(s) in the sitting ${sittingId}, as ${groups.length} sequential $batch chunk(s) of up to ${RELEASE_BATCH_SIZE}`, {
      note,
      refused: summary.refused,
      simulate: bulkOpts.simulate ?? null,
    });
    let done = 0;
    for (const [gi, group] of groups.entries()) {
      const label = `chunk ${gi + 1} of ${groups.length}`;
      say(`would PATCH ${label}: ${group.length} row(s), one If-Match each`, {
        requests: group.map((w) => ({ id: w.id, ifMatch: w.ifMatch, patch: w.patch, simulated: w.outcome })),
      });
      const landed = group.filter((w) => w.outcome === 'ok' || w.outcome === 'auditFail');
      say(`would POST the audit rows for ${label}: ${landed.reduce((n, w) => n + w.audit.length, 0)} draft(s) for ${landed.length} row(s)`, {
        drafts: landed.flatMap((w) => w.audit),
      });
      for (const w of group) {
        if (w.outcome === 'conflict') summary.conflicts.push(w.id);
        else if (w.outcome === 'throttled') {
          // Still 429 at the end: "to retry" only, never also "resent after a slow-down" (ISS-196).
          summary.failed.push({ id: w.id, status: 429, reason: 'throttled' });
        } else {
          summary.applied.push(w.id);
          summary.eTags[w.id] = practiceETag(w.ifMatch);
          if (w.outcome === 'auditFail') w.audit.forEach((draft) => summary.auditPending.push({ id: w.id, draft }));
        }
      }
      await sleep(PRACTICE_CHUNK_MS);
      done += group.length;
      bulkOpts.onProgress?.({ chunk: gi + 1, chunks: groups.length, done, total: wouldSend.length });
    }
    if (summary.failed.length) summary.retryAfterMs = bulkOpts.simulate?.retryAfterMs ?? 5000;
    await sleep(PRACTICE_LATENCY_MS);
    return summary;
  };

  const retryBulkAudit = async (
    _io: ActionIO,
    sittingId: string,
    pending: BulkSummary['auditPending'],
  ): Promise<BulkSummary['auditPending']> => {
    say(`would re-post ${pending.length} audit row(s) for the sitting ${sittingId}, de-duped on a fresh read`, {
      drafts: pending.map((p) => p.draft),
    });
    await sleep(PRACTICE_LATENCY_MS);
    return [];
  };

  const verifyVL = async (
    _io: ActionIO,
    row: VerificationRow,
    verdict: VlVerifyVerdict,
    actor: Actor,
    vOpts: VerifyOpts = {},
  ): Promise<CommitResult> => {
    // Built by the live builder, so a missing reason or a non-mutable field throws here too.
    const result = tagVlAudit(buildVlVerify(row, verdict, actor, vOpts), crypto.randomUUID());
    say(`would mark VER-${row.id} ${verdict}`, { itemId: row.id, ifMatch: row.eTag, patch: result.patch, audit: result.audit });
    await sleep(PRACTICE_LATENCY_MS);
    return { eTag: practiceETag(row.eTag) };
  };

  const verifyVLBatch = async (
    _io: ActionIO,
    rows: readonly VerificationRow[],
    verdict: VlVerifyVerdict,
    actor: Actor,
    vOpts: VerifyOpts = {},
  ): Promise<VlBatchSummary> => {
    const summary: VlBatchSummary = { verdict, applied: [], conflicts: [], failed: [], refused: [], auditPending: [] };
    const seen = new Set<number>();
    const wouldSend: Array<{ id: number; ifMatch: string; patch: Record<string, unknown>; audit: unknown[] }> = [];
    for (const row of rows) {
      if (seen.has(row.id)) continue;
      seen.add(row.id);
      try {
        const result = tagVlAudit(buildVlVerify(row, verdict, actor, vOpts), crypto.randomUUID());
        wouldSend.push({ id: row.id, ifMatch: row.eTag, patch: result.patch, audit: result.audit });
        summary.applied.push(row.id);
      } catch (err) {
        if (err instanceof VerbError) summary.refused.push({ id: row.id, code: err.code });
        else throw err;
      }
    }
    say(`would mark ${wouldSend.length} Verification Log row(s) ${verdict}`, { rows: wouldSend, refused: summary.refused });
    await sleep(PRACTICE_LATENCY_MS);
    return summary;
  };

  const correctVL = async (
    _io: ActionIO,
    row: VerificationRow,
    newVerdict: VlCorrectVerdict,
    reason: string,
    actor: Actor,
    cOpts: VlCorrectOpts = {},
  ): Promise<CommitResult> => {
    // Built by the same pure builder as live, so every refusal (window, giver, reason) holds here too.
    const { now, ...ctx } = cOpts;
    const result = tagVlAudit(vlCorrect(row, newVerdict, reason, actor, new Date(now ?? Date.now()), ctx), crypto.randomUUID());
    say(`would correct VER-${row.id} from ${row.verdict} to ${newVerdict}`, {
      itemId: row.id,
      ifMatch: row.eTag,
      patch: result.patch,
      audit: result.audit,
    });
    await sleep(PRACTICE_LATENCY_MS);
    return { eTag: practiceETag(row.eTag) };
  };

  const postVlAudit = async (_io: ActionIO, pending: VlAuditPending): Promise<VlAuditPending> => {
    say(`would re-post ${pending.reduce((n, p) => n + p.drafts.length, 0)} Verification Log audit row(s), de-duped on a fresh read`, {
      rows: pending.map((p) => ({ id: p.id, drafts: p.drafts })),
    });
    await sleep(PRACTICE_LATENCY_MS);
    return [];
  };

  // --- 13.2-07: incident follow-ups (nothing reaches Graph, nothing is kept) ---------------------------------------
  const createFollowUp: Writer['createFollowUp'] = async (_io, built) => {
    // The same shape check as live, so a create live would refuse is refused here too.
    assertFollowUpFields(built.fields);
    const ref = String(built.fields.ParentId ?? '');
    const audit = followUpAuditRow(built.audit, null, crypto.randomUUID());
    say(`would create a follow-up on ${ref} in the Action Register, then its audit row`, {
      list: 'Action Register',
      fields: { ...built.fields },
      then: { list: 'Action Register Audit Log', audit },
    });
    await sleep(PRACTICE_LATENCY_MS);
    return { id: null, ref: null };
  };

  const postFollowUpAudit: Writer['postFollowUpAudit'] = async (_io, drafts) => {
    say(`would re-post ${drafts.length} follow-up audit row(s), de-duped on a fresh read`, { drafts: [...drafts] });
    await sleep(PRACTICE_LATENCY_MS);
    return [];
  };

  // --- 13-23: register records (nothing reaches Graph) ---------------------------------------------------------
  const records = () => opts.practiceRecords ?? practiceRecordStore();
  const simulate = opts.simulate ?? defaultSimulate(opts.env ?? defaultEnv());
  /** Refs whose first save already showed the simulated 412 (a sitting's first run only, D-13-12-7). */
  const simulated = new Set<string>();

  const lodgeRecord: Writer['lodgeRecord'] = async (_io, register, lodge, actor) => {
    // The same gate and floor check as live: a refusal throws here too.
    checkGate({ verb: 'lodge', row: { id: 0, ref: '', eTag: '', register, fields: {} } }, actor);
    assertLodgeFields(register, lodge.fields);
    const store = records();
    const ref = store.nextRef(register);
    const siblings = lodge.siblings.map((d) => prepareSibling(d, ref, actor));
    const eTag = practiceETag(null);
    say(`would lodge a ${register} record, then ${siblings.length} sibling row(s) under ${ref}`, { fields: lodge.fields, siblings });
    await sleep(PRACTICE_LATENCY_MS);
    store.put({
      ref,
      register,
      eTag,
      fields: practiceStoredFields(lodge.fields),
      siblings,
      lodgedAt: new Date().toISOString(),
      upn: actor.upn.trim().toLowerCase(),
    });
    return { ref, eTag, auditPending: [] };
  };

  const commitRecordVerb: Writer['commitRecordVerb'] = async (_io, ref, eTag, result, actor, gate) => {
    const practice = isPracticeRef(ref);
    const canonical = practice ? parsePracticeRef(ref).ref : parseRef(ref).ref;
    checkGate(gate, actor, canonical);
    const siblings = result.siblings.map((d) => prepareSibling(d, canonical, actor));
    say(`would save ${gate.verb} on ${canonical}`, { ifMatch: eTag, patch: result.patch, siblings });
    await sleep(PRACTICE_LATENCY_MS);
    const flag = parseSimulateFlag(simulate() ?? null);
    if (flag?.conflicts && !simulated.has(canonical)) {
      simulated.add(canonical);
      const columns = Object.keys(result.patch);
      throw new RecordConflictError(new Error('simulated 412 (practice mode)'), gate.row, columns, columns);
    }
    const nextETag = practiceETag(eTag);
    if (practice) {
      const store = records();
      const rec = store.get(canonical);
      if (rec) store.put({ ...rec, eTag: nextETag, fields: { ...rec.fields, ...result.patch }, siblings: [...rec.siblings, ...siblings] });
    }
    return { eTag: nextETag, auditPending: [] };
  };

  // --- 13-32: Internal Audits (nothing reaches Graph) ---------------------------------------------------------
  const audits = () => opts.practiceAudits ?? practiceAuditStore();

  const scheduleAudit: Writer['scheduleAudit'] = async (_io, result, actor) => {
    // The same gate and column check as live: a refusal throws here too.
    checkAuditGate({ verb: 'schedule', row: null }, actor);
    assertAuditPatch('schedule', result.patch);
    const store = audits();
    const ref = store.nextRef();
    const siblings = result.siblings.map((d) => prepareAuditSibling(d, ref, actor));
    const eTag = practiceETag(null);
    say(`would schedule an internal audit, then ${siblings.length} sibling row(s) under ${ref}`, { fields: result.patch, siblings });
    await sleep(PRACTICE_LATENCY_MS);
    store.put({ ref, eTag, fields: { ...result.patch }, siblings, scheduledAt: new Date().toISOString(), upn: actor.upn.trim().toLowerCase() });
    return { ref, eTag, auditPending: [] };
  };

  const commitAuditVerb: Writer['commitAuditVerb'] = async (_io, ref, eTag, result, actor, gate) => {
    const parsed = readAuditRef(ref);
    if (!parsed) throw new Error(`practice writer: ${ref} is not an audit ref`);
    checkAuditGate(gate, actor, parsed.ref);
    assertAuditPatch(gate.verb, result.patch);
    const siblings = result.siblings.map((d) => prepareSibling(d, parsed.ref, actor));
    say(`would save ${gate.verb} on ${parsed.ref}`, { ifMatch: eTag, patch: result.patch, siblings });
    await sleep(PRACTICE_LATENCY_MS);
    const flag = parseSimulateFlag(simulate() ?? null);
    if (flag?.conflicts && !simulated.has(parsed.ref)) {
      simulated.add(parsed.ref);
      throw new AuditConflictError(new Error('simulated 412 (practice mode)'), gate.row, Object.keys(result.patch));
    }
    const nextETag = practiceETag(eTag);
    if (parsed.practice) {
      const store = audits();
      const a = store.get(parsed.ref);
      if (a) store.put({ ...a, eTag: nextETag, fields: { ...a.fields, ...result.patch }, siblings: [...a.siblings, ...siblings] });
    }
    return { eTag: nextETag, auditPending: [] };
  };

  // 13.2-10: a save's rows for another record (the kept record's Note). Kept on that practice record when it is one.
  const postRecordSiblings: Writer['postRecordSiblings'] = async (_io, ref, drafts, actor) => {
    const practice = isPracticeRef(ref);
    const canonical = practice ? parsePracticeRef(ref).ref : parseRef(ref).ref;
    const siblings = drafts.map((d) => prepareSibling(d, canonical, actor));
    say(`would post ${siblings.length} sibling row(s) on ${canonical}`, { siblings });
    await sleep(PRACTICE_LATENCY_MS);
    if (practice) {
      const store = records();
      const rec = store.get(canonical);
      if (rec) store.put({ ...rec, siblings: [...rec.siblings, ...siblings] });
    }
    return { auditPending: [] };
  };

  const postRecordAudit: Writer['postRecordAudit'] = async (_io, ref, drafts) => {
    say(`would re-post ${drafts.length} audit row(s) for ${ref}, de-duped on a fresh read`, { drafts });
    await sleep(PRACTICE_LATENCY_MS);
    return [];
  };

  // Practice never owes a row (nothing it does can fail after a landed write), and never posts a live owed one.
  const retryRecordAudit: Writer['retryRecordAudit'] = async () => ({
    posted: 0,
    stillOwed: [],
    skippedHeld: 0,
    durable: true,
    note: null,
  });

  return {
    mode: 'practice',
    lodgeRecord,
    commitRecordVerb,
    postRecordAudit,
    postRecordSiblings,
    retryRecordAudit,
    scheduleAudit,
    commitAuditVerb,
    commitVerb,
    findCommitted,
    releaseBatch: (_io, rows, note, actor, o) => bulk('release', rows, note, actor, o),
    retireBatch: (_io, rows, note, actor, o) => bulk('retire', rows, note, actor, o),
    retryBulkAudit,
    verifyVL,
    verifyVLBatch,
    correctVL,
    postVlAudit,
    createFollowUp,
    postFollowUpAudit,
    outboxBindings: (io) => ({
      commit: (entry, progress) => commitVerb(io, entry, progress),
      findCommitted,
      retryAfterMsFor: () => undefined,
    }),
    outboxStorage: () => practiceStorage(browserStorage()),
  };
}

/**
 * The writer for this build. Practice unless `VITE_QMS_LIVE_WRITES === 'true'`.
 * Pages call this (or `useActionRuntime().writer`) and never import the live functions directly.
 */
export function getWriter(opts: WriterOptions = {}): Writer {
  return writeMode(opts.env ?? defaultEnv()) === 'live' ? liveWriter(opts) : practiceWriter(opts);
}
